import {
  RaceCategory,
  RaceDuration,
  StintBias,
  TrackId,
} from './analytics/regulations';

export interface TrackConfig {
  name: string;
  url: string;
  host: string;
  wsPort: number;
  logFile: string;
}

export interface Config {
  tracks: TrackConfig[];
}

export interface TeamInfo {
  id: string;
  kartNumber: string;
  position: string;
  driver: string;
  teamName: string;
  category: string;
  sector1: string;
  sector2: string;
  sector3: string;
  lastLap: string;
  gap: string;
  interval: string;
  bestLap: string;
  onTrackTime: string;
  pitCount: string;
  inBox: boolean;
  boxTimeRemaining: string;
  pitEntryTime: number | null;
  pitDuration: string;
  previousValues: Map<string, string>;
  lastLapTimes: number[];
  lapCount: number;
  stintAverages: StintAverageRow[];
  driverSessionAverages: DriverSessionAverageRow[];
  // Methods
  updateFromChannel(channel: string, value: string): void;
  hasChanged(field: string): boolean;
  updatePitDuration(): void;
  updateLapTime(lapTime: string): void;
  clearLapTimes(): void;
  convertLapTimeToSeconds(lapTime: string): number | null;
  getAverageLapTime(): string;
  getAverageLapTime10(): string;
  toString(): string;
}

export interface KartInfo {
  number: string;
  position: string;
  lastTime: string;
  bestTime: string;
  lastUpdate: Date;
}

export interface LogMessage {
  timestamp: string;
  type: string;
  content: string;
}

export type MessageType = 'init' | 'gmt' | 'grid' | 'css' | 'data' | 'error' | 'system';

export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  width?: string;
  essential?: boolean; // Cannot be hidden
}

export type PitColumn = 'red' | 'green' | 'blue';

export type KartRating = 'fast' | 'medium' | 'slow' | 'unknown';

export interface PitAssignment {
  teamId: string;
  kartNumber: string;
  driver: string;
  column: PitColumn;
  assignedAt: number;
  averageTime?: string;
  rating: KartRating;
  isManuallyAdded: boolean; // Track if kart was manually added (not auto-detected from pit status)
  isBlankKart: boolean; // Track if this is a blank/custom kart not tied to any team
  // New fields for persistent blank kart slots
  isTemporarilyOccupied?: boolean; // True when a blank kart is temporarily occupied by a team
  occupiedByTeam?: string; // The actual team ID when occupied
  originalBlankKartInfo?: { // Store original blank kart info when temporarily occupied
    kartNumber: string;
    driver: string;
    assignedAt: number;
  };
}

/** @deprecated Superseded by PitBoard/PitSlot; retained to migrate stored boards. */
export interface PitAssignments {
  red: PitAssignment[];
  green: PitAssignment[];
  blue: PitAssignment[];
}

/** The team currently occupying a pit slot. Absent means the box is free. */
export interface PitSlotOccupant {
  teamId: string;
  driver: string;
  /** Copied in when the team takes the box, so the record survives the session. */
  teamName?: string;
  /** Apex category at the time: pro, gold or silver. */
  category?: string;
  since: number;
}

/**
 * One box in the pit lane. The slot describes the *kart* standing there — its
 * number, speed rating and lap average — while `occupant` records which team, if
 * any, currently has it. Keeping the two apart means `slotId` is always a slot and
 * never a team, which is what the previous single-shape model got wrong.
 */
export interface PitSlot {
  slotId: string;
  kartNumber: string;
  column: PitColumn;
  createdAt: number;
  rating: KartRating;
  averageTime?: string;
  /** Whether a person put this box on the board or it came from a kart swap. */
  origin: 'manual' | 'auto';
  occupant?: PitSlotOccupant;
  /**
   * Who had the kart last, once the box is free again. A free kart is only as
   * good as the pace of whoever just climbed out of it.
   */
  lastOccupant?: PitSlotOccupant;
}

export interface PitBoard {
  red: PitSlot[];
  green: PitSlot[];
  blue: PitSlot[];
}

export type SortMode = 'position' | 'average' | 'average10' | 'best' | 'category';

export interface StintAverageRow {
  driver: string;
  lapCount: number;
  averageLap: string;
  kind: 'completed' | 'current';
  /**
   * @deprecated No longer sent — laps arrive once each via `teamLapAppended`.
   * Present only in events recorded before that change, where it seeds the
   * client-side lap history so those recordings still replay in full.
   */
  lapTimesSeconds?: number[];
}

export interface DriverSessionAverageRow {
  driver: string;
  lapCount: number;
  averageLap: string;
}

/** Session countdown — absolute end time (epoch ms) ticked locally as `endsAt - now`. */
export interface SessionTimer {
  endsAt: number;
}

/**
 * Per-event regulations, resolved by the backend (never partial on the wire).
 * Broadcast to every client because the pit window is viewer-visible; only
 * editing them is admin-only.
 */
export interface RaceRules {
  maxStintMinutes: number;
  dueSoonMinutes: number;
  requiredStops: number | null;
  minDriverMinutes: number | null;
  maxDriverMinutes: number | null;
  /** Shortest legal time in the pit lane per stop; null when unconstrained. */
  minPitSeconds: number | null;
  /** Shortest legal stint, Pit Out to Pit In; null when unconstrained. */
  minStintMinutes: number | null;
}

export const DEFAULT_RACE_RULES: RaceRules = {
  maxStintMinutes: 70,
  dueSoonMinutes: 10,
  requiredStops: null,
  minDriverMinutes: null,
  maxDriverMinutes: null,
  minPitSeconds: null,
  minStintMinutes: null,
};

/** A driver in the planned running order. Position in the array is the order. */
export interface PlannedDriver {
  name: string;
  targetStintMinutes: number | null;
  /** Overrides the event-wide driver bounds for this driver. */
  minMinutes: number | null;
  maxMinutes: number | null;
}

export interface TeamPlan {
  teamId: string;
  drivers: PlannedDriver[];
}

/** Admin-only: the running order each team intends to use. */
export interface RacePlan {
  teams: TeamPlan[];
}

export const EMPTY_RACE_PLAN: RacePlan = { teams: [] };

/** The event currently being recorded, or null when nothing is. */
export interface ActiveEvent {
  id: string;
  title: string;
}

/** One entry in Apex's race-control feed (warnings, penalties, messages). */
export interface RaceControlEntry {
  time: string;
  flag: string;
  kartNumber: string;
  category: string;
  text: string;
}

/** Plain serialisable team object received from the backend via Socket.IO */
export interface TeamData {
  id: string;
  kartNumber: string;
  position: string;
  driver: string;
  teamName: string;
  category: string;
  sector1: string;
  sector2: string;
  sector3: string;
  lastLap: string;
  gap: string;
  interval: string;
  bestLap: string;
  onTrackTime: string;
  pitCount: string;
  inBox: boolean;
  boxTimeRemaining: string;
  pitEntryTime: number | null;
  pitDuration: string;
  lastLapTimes: number[];
  lapCount: number;
  averageLap: string;
  stintAverages: StintAverageRow[];
  driverSessionAverages: DriverSessionAverageRow[];
}

export interface WebSocketHookReturn {
  isConnected: boolean;
  teams: Map<string, TeamInfo>;
  karts: Map<string, KartInfo>;
  messages: LogMessage[];
  connect: () => void;
  disconnect: () => void;
  toggleSortMode: () => void;
  sortMode: SortMode;
}

export const CHANNEL_TYPE_MAP = new Map([
  ['dr', 'driver'],
  ['s1', 'sector1'],
  ['s2', 'sector2'],
  ['s3', 'sector3'],
  ['llp', 'lastLap'],
  ['gap', 'gap'],
  ['int', 'interval'],
  ['blp', 'bestLap'],
  ['otr', 'onTrackTime'],
  ['pit', 'pitCount'],
  ['cat', 'category']
]);

export const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: 'currentPosition', label: '#',        visible: true,  width: '4%',  essential: true },
  { key: 'teamName',        label: 'Team (driver)', visible: true,  width: '20%', essential: true },
  { key: 'avgLap10',        label: 'Avg 10',   visible: true,  width: '10%' },
  { key: 'avgGap10',        label: 'Gap 10',   visible: true,  width: '7%' },
  { key: 'avgLap',          label: 'Avg 5',    visible: true,  width: '10%', essential: true },
  { key: 'avgGap',          label: 'Gap 5',    visible: true,  width: '7%' },
  { key: 'cleanAvg',        label: 'Clean Avg', visible: true, width: '10%' },
  { key: 'paceTrend',       label: 'Trend',    visible: true,  width: '6%' },
  { key: 'consistency',     label: 'Consist.',  visible: true, width: '8%' },
  { key: 'bestLap',         label: 'Best Lap', visible: true,  width: '10%' },
  { key: 'lastLap',         label: 'Last Lap', visible: true,  width: '10%', essential: true },
  { key: 'position',        label: 'Pos',      visible: false, width: '4%',  essential: true },
  { key: 'kartNumber',      label: 'Kart',     visible: false, width: '5%' },
  { key: 'team',            label: 'Team ID',  visible: false, width: '7%',  essential: true },
  { key: 'category',        label: 'Category', visible: true,  width: '7%' },
  { key: 'gap',             label: 'Gap',      visible: false, width: '7%' },
  { key: 'interval',        label: 'Int',      visible: false, width: '6%' },
  { key: 'sectors',         label: 'Sectors',  visible: false, width: '12%' },
  { key: 'onTrackTime',     label: 'On Track', visible: false, width: '7%' },
  { key: 'pitCount',        label: 'Pits',     visible: false, width: '4%' },
  { key: 'boxStatus',       label: 'Box',      visible: false, width: '7%' },
  { key: 'stintAvgs',       label: 'Stint avg',    visible: false, width: '14%' },
  { key: 'driverAvgs',      label: 'Driver Δ',     visible: false, width: '14%' },
  { key: 'consistency5',    label: 'Consist. 5',   visible: false, width: '8%' },
  { key: 'consistency10',   label: 'Consist. 10',  visible: false, width: '8%' },
]; 
/**
 * A saved team strategy — the pre-race stint plan.
 *
 * Stored server-side as jsonb and validated there for shape only; whether a plan
 * is *legal* is decided by `analytics/regulations.ts` on this side, so the rules
 * can change without a backend release.
 */
export interface StrategyConfig {
  durationHours: RaceDuration;
  category: RaceCategory;
  trackId: TrackId;
  /** Wall-clock race start, ISO 8601 — the sheet's Start/End columns derive from it. */
  startsAt: string;
  /** Overrides the mandatory stop count when a team reads rule 10 differently. */
  requiredStopsOverride: number | null;
  /** False when the bulletin declares one direction (rain): no midpoint closure. */
  directionChange: boolean;
  /**
   * Where the long stints go. The stop count is fixed by rule 10, so this only
   * moves driving time around — it cannot buy you fewer stops.
   */
  stintBias: StintBias;
  /** Run at the maximum stint time for as many stints as the rules allow. */
  maximiseStintTime: boolean;
  /** PRO/GOLD: keep a driver in for two stints, changing only the kart between. */
  preferDoubleStints: boolean;
  /**
   * Clear air kept either side of every pit lane closure. Prudence, not
   * regulation — a stop inside it is a warning, never a penalty.
   */
  pitMarginMinutes: number;
}

export interface StrategyDriver {
  name: string;
  /** Skipped by auto-assignment — not arrived yet, or resting. */
  unavailable: boolean;
}

/** What the pit stop *following* a stint changes. */
export type StopKind = 'driver-and-kart' | 'kart-only' | 'none';

export interface PlannedStint {
  /** Minutes from the race start to this stint's Pit Out. */
  startMinute: number;
  /** Driving time, Pit Out → Pit In, in minutes. */
  minutes: number;
  /** Index into `StrategyPlan.roster`; null when nobody is assigned yet. */
  driverIndex: number | null;
  stopKind: StopKind;
  note: string | null;
  /**
   * Set when somebody typed this stint's length. A live re-plan spreads a long or
   * short stint over the rest of the race but leaves pinned lengths alone.
   */
  pinned?: boolean;
}

/**
 * What actually happened in one stint, as watched from the timing feed. Kept
 * apart from the planned stints, and merged rather than replaced, so any number
 * of browsers can record the same race without overwriting each other.
 */
export interface StintActual {
  /** Race minute the stint's Pit Out happened. */
  startMinute?: number;
  /** Race minute the stint's Pit In happened. */
  pitInMinute?: number;
  /** Driver label as the feed showed it, without the stint clock. */
  driver?: string;
}

/** Keyed by stint index as a string, because that is what JSON gives back. */
export type StrategyActuals = Record<string, StintActual>;

/** An account a plan is shared with. */
export interface SharedAccount {
  id: string;
  username: string;
  displayName: string;
}

export interface StrategyPlan {
  id: string;
  title: string;
  config: StrategyConfig;
  roster: StrategyDriver[];
  stints: PlannedStint[];
  /**
   * The recorded race this plan is for, or null while it is just a plan. Set it
   * to line the sheet up with an event — live, so the rules and roster can be
   * pushed at it, or past, so the plan sits with the race it was written for.
   */
  eventId: string | null;
  /**
   * The Apex grid row the plan runs for. Stable within one event; a new event
   * hands out new rows, so a reused plan has to be pointed at its team again.
   */
  teamId: string | null;
  /** The kart the team had when the plan was attached. A hint: karts get swapped. */
  kartNumber: string | null;
  /** What the timing feed showed actually happening, per stint index. */
  actuals: StrategyActuals;
  ownerId: string | null;
  ownerName: string | null;
  /** Accounts besides the owner who can open and edit it. */
  sharedWith: SharedAccount[];
  updatedAt: string;
}

/** What the list endpoint returns — no stints, so the index stays cheap. */
export interface StrategySummary {
  id: string;
  title: string;
  config: StrategyConfig;
  driverCount: number;
  stintCount: number;
  eventId: string | null;
  teamId: string | null;
  kartNumber: string | null;
  ownerId: string | null;
  ownerName: string | null;
  sharedCount: number;
  updatedAt: string;
}

/** One driver on the all-time leaderboard, aggregated over the filtered events. */
export interface LeaderboardRow {
  driverId: string;
  displayName: string;
  /** All-time rating, whatever the filters. */
  rating: number;
  ratedEvents: number;
  events: number;
  qualifiedEvents: number;
  laps: number;
  cleanLaps: number;
  bestSeconds: number | null;
  cleanAverageSeconds: number | null;
  /** Clean average ÷ the event's fastest; 1.0 is the quickest, averaged over events. */
  paceIndex: number | null;
  bestIndex: number | null;
  consistencyCv: number | null;
  lastSeenAt: string | null;
}

export interface Leaderboard {
  rows: LeaderboardRow[];
  tracks: string[];
  years: number[];
  defaultTrack: string | null;
  minLaps: number;
  qualifyingCleanLaps: number;
  rankedEvents: number;
}

export interface DriverAliasInfo {
  id: string;
  label: string;
  aliasKey: string;
}

export interface DriverEventRow {
  eventId: string;
  title: string;
  trackName: string;
  startedAt: string;
  lapCount: number;
  cleanLapCount: number;
  bestSeconds: number | null;
  cleanAverageSeconds: number | null;
  consistencyCv: number | null;
  paceIndex: number | null;
  bestIndex: number | null;
  ratingBefore: number;
  ratingAfter: number;
  qualified: boolean;
}

export interface DriverDetail {
  id: string;
  displayName: string;
  rating: number;
  ratedEvents: number;
  aliases: DriverAliasInfo[];
  events: DriverEventRow[];
}
